import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthLayout, Field } from "@/components/AuthLayout";
import { useAuth, homeFor } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { GPS_CONSENT_TEXT, GPS_CONSENT_NOTE } from "@/components/GpsConsentScreen";

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: ["driver", "business"].includes(params.get("role")) ? params.get("role") : "customer", vehicle: "", gps_consent: false, business_name: "", store_address: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await register(form);
      nav(u.role === "customer" && !u.has_valid_card ? "/customer/add-card" : homeFor(u.role));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Create your account" subtitle="Ride in comfort, or drive with distinction.">
      <div className="mb-8 grid grid-cols-3 rounded-full border border-amber-500/20 p-1" role="tablist">
        {["customer", "driver", "business"].map((r) => (
          <button key={r} type="button" data-testid={`signup-role-${r}`} onClick={() => setForm({ ...form, role: r })}
            className={`rounded-full py-2.5 text-xs font-medium transition-colors duration-300 sm:text-sm ${form.role === r ? "bg-amber-400 text-black" : "text-zinc-400 hover:text-amber-100"}`}>
            {r === "customer" ? "I want to ride" : r === "driver" ? "I want to drive" : "I'm a store"}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
        {form.role === "business" && <Field label="Business name" required data-testid="signup-business-name-input" value={form.business_name} onChange={set("business_name")} />}
        {form.role === "business" && <Field label="Store address" required data-testid="signup-store-address-input" placeholder="2500 Cedar Springs Rd, Dallas, TX" value={form.store_address} onChange={set("store_address")} />}
        <Field label={form.role === "business" ? "Contact name" : "Full name"} required data-testid="signup-name-input" value={form.name} onChange={set("name")} />
        <Field label="Email" type="email" required data-testid="signup-email-input" value={form.email} onChange={set("email")} />
        <Field label="Phone" type="tel" required minLength={5} data-testid="signup-phone-input" value={form.phone} onChange={set("phone")} />
        <Field label="Password" type="password" required minLength={6} data-testid="signup-password-input" value={form.password} onChange={set("password")} />
        {form.role === "driver" && <Field label="Vehicle (make, model, plate)" data-testid="signup-vehicle-input" placeholder="White Nissan Armada Platinum · ABC-123" value={form.vehicle} onChange={set("vehicle")} />}
        {form.role === "customer" && (
          <div>
            <label className="flex cursor-pointer items-start gap-3 text-sm text-zinc-200">
              <input type="checkbox" required data-testid="signup-gps-consent-checkbox" checked={form.gps_consent} onChange={(e) => setForm({ ...form, gps_consent: e.target.checked })} className="mt-0.5 h-4 w-4 accent-amber-400" />
              {GPS_CONSENT_TEXT}
            </label>
            <p className="mt-2 pl-7 text-xs text-zinc-500" data-testid="signup-gps-note">{GPS_CONSENT_NOTE}</p>
          </div>
        )}
        <p className="text-xs text-zinc-500" data-testid="signup-legal-links">By signing up you agree to our <Link to="/terms" className="text-amber-300">Terms</Link> and <Link to="/privacy" className="text-amber-300">Privacy Policy</Link>.</p>
        {error && <p data-testid="signup-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>}
        <button type="submit" disabled={busy} data-testid="signup-submit-button" className="btn-gold w-full">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create account
        </button>
      </form>
      <p className="mt-8 text-sm text-zinc-400">
        Already a member? <Link to="/login" data-testid="goto-login-link" className="text-amber-300 hover:text-amber-200">Sign in</Link>
      </p>
    </AuthLayout>
  );
}
